/**
 * Support Tickets API Client
 */

import { apiRequest } from '../queryClient';
import { compressImage } from '@/utils/image-compression';

export interface TicketTag {
  id: number;
  name: string;
  color: string | null;
}

export interface SupportTicket {
  id: number;
  ticketNumber: string;
  customerId: number | null;
  installationId: number | null;
  customerName: string | null;
  customerPhone: string | null;
  customerEmail: string | null;
  customerAddress: string | null;
  displayCustomerName?: string;
  displayCustomerEmail?: string;
  displayCustomerPhone?: string;
  systemProvider: string | null;
  providerId: number | null;
  issueDescription: string;
  issueCategory: string | null;
  status: string;
  priority: string;
  providerTicketRef: string | null;
  escalatedAt: string | null;
  resolution: string | null;
  resolvedAt: string | null;
  resolvedBy: number | null;
  createdBy: number | null;
  assignedTo: number | null;
  createdAt: string;
  updatedAt: string;
  linkedCustomerName?: string;
  linkedCustomerEmail?: string;
  linkedCustomerPhone?: string;
  activities?: SupportTicketActivity[];
  tags?: TicketTag[];
  // Email automation fields
  sourceEmail?: string | null;
  sourceEmailMessageId?: string | null;
  lastCustomerEmailAt?: string | null;
  originalSubject?: string | null;
}

export interface SupportTicketActivity {
  id: number;
  ticketId: number;
  activityType: string;
  content: string;
  activityTime: string | null;
  isResolutionStep: boolean;
  createdBy: number | null;
  createdAt: string;
  createdByName?: string;
}

export interface SupportTicketImage {
  id: number;
  ticketId: number;
  filename: string;
  originalFilename: string;
  objectKey: string;
  mimeType: string;
  size: number;
  uploadedBy: number | null;
  createdAt: string;
}

export interface EquipmentProvider {
  id: number;
  name: string;
  contactPhone: string | null;
  contactEmail: string | null;
  supportUrl: string | null;
  notes: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SimilarSolution {
  ticketId: number;
  ticketNumber: string;
  similarity: number;
  issueDescription: string;
  resolution: string;
  provider: string | null;
  resolvedAt: string | null;
  resolutionSteps: string[];
}

export interface SuggestionResult {
  suggestions: SimilarSolution[];
  aiAvailable: boolean;
  message?: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    totalPages: number;
    totalCount: number;
    hasNext: boolean;
    hasPrevious: boolean;
  };
}

export interface TicketStats {
  open: number;
  inProgress: number;
  escalated: number;
  resolvedThisMonth: number;
}

// Ticket CRUD operations
export async function fetchTickets(params: {
  page?: number;
  limit?: number;
  search?: string;
  statuses?: string[];
  priority?: string;
  provider?: string;
  tags?: number[];
}): Promise<PaginatedResponse<SupportTicket>> {
  const queryParams = new URLSearchParams();
  if (params.page) queryParams.set('page', params.page.toString());
  if (params.limit) queryParams.set('limit', params.limit.toString());
  if (params.search) queryParams.set('search', params.search);
  if (params.statuses && params.statuses.length > 0) {
    queryParams.set('statuses', params.statuses.join(','));
  }
  if (params.priority) queryParams.set('priority', params.priority);
  if (params.provider) queryParams.set('provider', params.provider);
  if (params.tags && params.tags.length > 0) {
    queryParams.set('tags', params.tags.join(','));
  }

  const response = await fetch(`/api/support-tickets?${queryParams}`, {
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to fetch tickets');
  }

  return response.json();
}

export async function fetchTicket(id: number): Promise<SupportTicket> {
  const response = await fetch(`/api/support-tickets/${id}`, {
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to fetch ticket');
  }

  return response.json();
}

export async function createTicket(data: Partial<SupportTicket>): Promise<SupportTicket> {
  const response = await fetch('/api/support-tickets', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error('Failed to create ticket');
  }

  return response.json();
}

export async function updateTicket(
  id: number,
  data: Partial<SupportTicket>
): Promise<SupportTicket> {
  const response = await fetch(`/api/support-tickets/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error('Failed to update ticket');
  }

  return response.json();
}

export async function deleteTicket(id: number): Promise<void> {
  const response = await fetch(`/api/support-tickets/${id}`, {
    method: 'DELETE',
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to delete ticket');
  }
}

// Activity operations
export async function addActivity(
  ticketId: number,
  data: {
    activityType: string;
    content: string;
    activityTime?: string;
    isResolutionStep?: boolean;
  }
): Promise<SupportTicketActivity> {
  const response = await fetch(`/api/support-tickets/${ticketId}/activities`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error('Failed to add activity');
  }

  return response.json();
}

export async function sendTicketReply(
  ticketId: number,
  data: {
    content: string;
  }
): Promise<SupportTicketActivity | null> {
  const response = await fetch(`/api/support-tickets/${ticketId}/reply`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => null);
    throw new Error(errorBody?.error || 'Failed to send customer reply');
  }

  const result = await response.json();
  return result.activity;
}

// Provider operations
export async function fetchProviders(activeOnly = true): Promise<EquipmentProvider[]> {
  const response = await fetch(
    `/api/support-tickets/providers?active=${activeOnly}`,
    { credentials: 'include' }
  );

  if (!response.ok) {
    throw new Error('Failed to fetch providers');
  }

  return response.json();
}

export async function createProvider(
  data: Partial<EquipmentProvider>
): Promise<EquipmentProvider> {
  const response = await fetch('/api/support-tickets/providers', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error('Failed to create provider');
  }

  return response.json();
}

// AI suggestions
export async function findSimilarSolutions(
  ticketId: number
): Promise<SuggestionResult> {
  const response = await fetch(`/api/support-tickets/${ticketId}/similar`, {
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to find similar solutions');
  }

  return response.json();
}

export async function searchSolutions(
  issueDescription: string,
  provider?: string
): Promise<SuggestionResult> {
  const response = await fetch('/api/support-tickets/search-solutions', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ issueDescription, provider }),
  });

  if (!response.ok) {
    throw new Error('Failed to search solutions');
  }

  return response.json();
}

// Statistics
export async function fetchTicketStats(): Promise<TicketStats> {
  const response = await fetch('/api/support-tickets/stats/overview', {
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to fetch ticket stats');
  }

  return response.json();
}

// Tag operations
export async function fetchAllTags(search?: string): Promise<TicketTag[]> {
  const params = search ? `?search=${encodeURIComponent(search)}` : '';
  const response = await fetch(`/api/support-tickets/tags${params}`, {
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to fetch tags');
  }

  return response.json();
}

export async function createTag(data: {
  name: string;
  color?: string;
}): Promise<TicketTag> {
  const response = await fetch('/api/support-tickets/tags', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error('Failed to create tag');
  }

  return response.json();
}

export async function updateTicketTags(
  ticketId: number,
  tagIds: number[]
): Promise<TicketTag[]> {
  const response = await fetch(`/api/support-tickets/${ticketId}/tags`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ tagIds }),
  });

  if (!response.ok) {
    throw new Error('Failed to update ticket tags');
  }

  return response.json();
}

// Related tickets by tags
export interface RelatedTicket {
  id: number;
  ticketNumber: string;
  status: string;
  priority: string;
  resolution: string | null;
  smartSubject: string | null;
  issueDescription: string;
  sharedTagCount: number;
  sharedTags: TicketTag[];
}

export async function fetchRelatedByTags(ticketId: number): Promise<RelatedTicket[]> {
  const response = await fetch(`/api/support-tickets/${ticketId}/related-by-tags`, {
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to fetch related tickets');
  }

  return response.json();
}

// Image operations
export async function fetchTicketImages(ticketId: number): Promise<SupportTicketImage[]> {
  const response = await fetch(`/api/support-tickets/${ticketId}/images`, {
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to fetch ticket images');
  }

  return response.json();
}

export async function uploadTicketImage(ticketId: number, file: File): Promise<SupportTicketImage> {
  // Compress and convert image (handles HEIC→JPEG conversion for iPhone photos)
  const processedFile = await compressImage(file, {
    maxWidth: 1920,
    maxHeight: 1920,
    quality: 0.85,
    maxSizeMB: 5,
    outputFormat: 'jpeg',
  });

  const formData = new FormData();
  formData.append('image', processedFile);

  const response = await fetch(`/api/support-tickets/${ticketId}/images`, {
    method: 'POST',
    credentials: 'include',
    body: formData,
  });

  if (!response.ok) {
    throw new Error('Failed to upload image');
  }

  return response.json();
}

export async function deleteTicketImage(ticketId: number, imageId: number): Promise<void> {
  const response = await fetch(`/api/support-tickets/${ticketId}/images/${imageId}`, {
    method: 'DELETE',
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to delete image');
  }
}

export function getTicketImageUrl(ticketId: number, imageId: number): string {
  return `/api/support-tickets/${ticketId}/images/${imageId}/file`;
}
